import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import api, { authFetch } from '../services/api';
import { useTheme } from '../context/ThemeContext';

const IntegrationsContainer = styled.div`
  /* Container styling handled by Layout component */
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  flex-wrap: wrap;
  gap: 1rem;

  h1 {
    margin: 0;
    color: ${props => props.$isDark ? '#f9fafb' : '#333'};
    font-size: 2rem;
  }
`;

const RefreshButton = styled.button`
  background: var(--primary-600);
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 500;
  transition: all 0.3s ease;

  &:hover {
    background: var(--primary-700);
    transform: translateY(-2px);
  }

  &:disabled {
    background: #ccc;
    cursor: not-allowed;
    transform: none;
  }
`;

const IntegrationsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
`;

const IntegrationCard = styled.div`
  background: ${props => props.$isDark ? '#1f2937' : 'white'};
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  border: 1px solid ${props => props.$isDark ? '#374151' : '#e1e8ed'};
  transition: all 0.3s ease;

  &:hover {
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
    transform: translateY(-2px);
  }
`;

const IntegrationHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1rem;

  h3 {
    margin: 0;
    color: ${props => props.$isDark ? '#f9fafb' : '#333'};
    font-size: 1.2rem;
  }
`;

const StatusBadge = styled.span`
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 500;
  background: ${props => {
    switch (props.status) {
      case 'connected': return '#d4edda';
      case 'error': return '#f8d7da';
      case 'testing': return '#fff3cd';
      default: return '#e2e3e5';
    }
  }};
  color: ${props => {
    switch (props.status) {
      case 'connected': return '#155724';
      case 'error': return '#721c24';
      case 'testing': return '#856404';
      default: return '#6c757d';
    }
  }};
`;

const IntegrationInfo = styled.div`
  margin-bottom: 1rem;

  p {
    margin: 0 0 0.5rem 0;
    color: ${props => props.$isDark ? '#9ca3af' : '#666'};
    font-size: 0.9rem;
  }

  .auth-type {
    font-weight: 500;
    color: ${props => props.$isDark ? '#e5e7eb' : '#333'};
  }
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;

  button {
    padding: 0.5rem 1rem;
    border: none;
    border-radius: 6px;
    cursor: pointer;
    font-size: 0.8rem;
    font-weight: 500;
    transition: all 0.3s ease;
    min-width: 80px;

    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }
  }

  .connect-btn {
    background: #28a745;
    color: white;

    &:hover:not(:disabled) {
      background: #218838;
    }
  }

  .test-btn {
    background: #17a2b8;
    color: white;

    &:hover:not(:disabled) {
      background: #138496;
    }
  }

  .sync-btn {
    background: #ffc107;
    color: #212529;

    &:hover:not(:disabled) {
      background: #e0a800;
    }
  }

  .disconnect-btn {
    background: #dc3545;
    color: white;

    &:hover:not(:disabled) {
      background: #c82333;
    }
  }

  .configure-btn {
    background: #6f42c1;
    color: white;

    &:hover:not(:disabled) {
      background: #5a32a3;
    }
  }
`;

const ConnectionDetails = styled.div`
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid ${props => props.$isDark ? '#374151' : '#eee'};
  font-size: 0.8rem;

  .detail-row {
    display: flex;
    justify-content: space-between;
    margin-bottom: 0.25rem;
    
    .label {
      color: ${props => props.$isDark ? '#9ca3af' : '#666'};
    }
    
    .value {
      color: ${props => props.$isDark ? '#e5e7eb' : '#333'};
      font-weight: 500;
    }
  }
`;

const ConfigModal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: ${props => props.$isDark ? '#1f2937' : 'white'};
  padding: 2rem;
  border-radius: 12px;
  width: 90%;
  max-width: 500px;
  max-height: 80vh;
  overflow-y: auto;

  h3 {
    margin: 0 0 1.5rem 0;
    color: ${props => props.$isDark ? '#f9fafb' : '#333'};
  }

  .form-group {
    margin-bottom: 1rem;

    label {
      display: block;
      margin-bottom: 0.5rem;
      font-weight: 500;
      color: ${props => props.$isDark ? '#e5e7eb' : '#333'};
    }

    input, textarea {
      width: 100%;
      padding: 0.75rem;
      border: 1px solid ${props => props.$isDark ? '#4b5563' : '#ddd'};
      border-radius: 6px;
      font-size: 0.9rem;
      box-sizing: border-box;
      background: ${props => props.$isDark ? '#374151' : 'white'};
      color: ${props => props.$isDark ? '#f9fafb' : 'inherit'};

      &:focus {
        outline: none;
        border-color: #6c5ce7;
        box-shadow: 0 0 0 3px rgba(108, 92, 231, 0.1);
      }
    }
  }

  .modal-actions {
    display: flex;
    gap: 1rem;
    justify-content: flex-end;
    margin-top: 1.5rem;

    button {
      padding: 0.75rem 1.5rem;
      border: none;
      border-radius: 6px;
      cursor: pointer;
      font-weight: 500;
      transition: all 0.3s ease;

      &.primary {
        background: #6c5ce7;
        color: white;

        &:hover {
          background: #5f3dc4;
        }
      }

      &.secondary {
        background: #6c757d;
        color: white;

        &:hover {
          background: #5a6268;
        }
      }
    }
  }
`;

const LoadingSpinner = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 200px;

  .spinner {
    width: 40px;
    height: 40px;
    border: 4px solid #f3f3f3;
    border-top: 4px solid #6c5ce7;
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const ErrorMessage = styled.div`
  background: #f8d7da;
  color: #721c24;
  padding: 1rem;
  border-radius: 8px;
  margin-bottom: 1rem;
  border: 1px solid #f5c6cb;
`;

const SuccessMessage = styled.div`
  background: #d4edda;
  color: #155724;
  padding: 1rem;
  border-radius: 8px;
  margin-bottom: 1rem;
  border: 1px solid #c3e6cb;
`;

const Integrations = () => {
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const [integrations, setIntegrations] = useState([]);
  const [aiIntegrations, setAiIntegrations] = useState([]);
  const [marketplaceIntegrations, setMarketplaceIntegrations] = useState([]);
  const [statuses, setStatuses] = useState({});
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState({});
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [configPlatform, setConfigPlatform] = useState(null);
  const [configForm, setConfigForm] = useState({ api_key: '', additional_config: {} });

  useEffect(() => {
    loadIntegrations();
    loadAiIntegrations();
    loadMarketplaceIntegrations();
  }, []);

  const loadIntegrations = async () => {
    try {
      setLoading(true);
      setError('');
      
      // Load available integrations using api service
      const integrationsResponse = await api.get('/marketing/integrations/available');
      setIntegrations(integrationsResponse.data.integrations || []);

      // Load connection statuses
      try {
        const statusResponse = await api.get('/marketing/integrations/status');
        setStatuses(statusResponse.data.statuses || {});
      } catch (statusErr) {
        console.warn('Could not load integration statuses:', statusErr);
        // Non-fatal, continue with empty statuses
      }

    } catch (err) {
      console.error('Integration load error:', err);
      const errorMessage = err.response?.data?.error || err.response?.data?.message || err.message;
      setError(`Failed to load integrations: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  const loadAiIntegrations = async () => {
    try {
      const response = await api.get('/integrations?type=ai');
      setAiIntegrations(response.data.integrations || []);
    } catch (err) {
      console.error('Failed to load AI integrations:', err);
      console.error('Error response:', err.response);
      // Don't show error to user for load failures, just log it
    }
  };

  const loadMarketplaceIntegrations = async () => {
    try {
      const response = await api.get('/integrations?type=marketplace');
      setMarketplaceIntegrations(response.data.integrations || []);
    } catch (err) {
      console.error('Failed to load marketplace integrations:', err);
      // Don't show error to user for load failures, just log it
    }
  };

  const handleAddAiIntegration = () => {
    navigate('/integrations/ai-providers/new');
  };

  const handleEditAiIntegration = (integration) => {
    navigate(`/integrations/ai-providers/${integration.id}/edit`);
  };

  const handleDeleteAiIntegration = async (id) => {
    if (!window.confirm('Are you sure you want to delete this AI integration?')) {
      return;
    }

    try {
      await api.delete(`/integrations/${id}`);
      setSuccess('AI integration deleted successfully');
      await loadAiIntegrations();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete AI integration');
    }
  };

  const handleTestAiIntegration = async (id) => {
    try {
      setActionLoading(prev => ({ ...prev, [`ai_${id}`]: 'testing' }));
      const response = await api.post(`/integrations/${id}/test`);
      
      if (response.data.success) {
        setSuccess(response.data.message || 'Connection test successful');
      } else {
        setError('Connection test failed');
      }
      
      await loadAiIntegrations();
    } catch (err) {
      setError(err.response?.data?.error || 'Test failed');
    } finally {
      setActionLoading(prev => ({ ...prev, [`ai_${id}`]: null }));
    }
  };

  const handleAddMarketplaceIntegration = () => {
    navigate('/integrations/marketplace/new');
  };

  const handleEditMarketplaceIntegration = (integration) => {
    navigate(`/integrations/marketplace/${integration.id}/edit`);
  };

  const handleDeleteMarketplaceIntegration = async (id) => {
    if (!window.confirm('Are you sure you want to delete this marketplace integration?')) {
      return;
    }

    try {
      await api.delete(`/integrations/${id}`);
      setSuccess('Marketplace integration deleted successfully');
      await loadMarketplaceIntegrations();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete marketplace integration');
    }
  };

  const handleTestMarketplaceIntegration = async (id) => {
    try {
      setActionLoading(prev => ({ ...prev, [`marketplace_${id}`]: 'testing' }));
      const response = await api.post(`/integrations/${id}/test`);
      
      if (response.data.success) {
        setSuccess(response.data.message || 'Connection test successful');
      } else {
        setError('Connection test failed');
      }
      
      await loadMarketplaceIntegrations();
    } catch (err) {
      setError(err.response?.data?.error || 'Test failed');
    } finally {
      setActionLoading(prev => ({ ...prev, [`marketplace_${id}`]: null }));
    }
  };

  const handleConnect = async (platform, authType) => {
    if (authType === 'oauth2') {
      // Handle OAuth flow
      const redirectUri = `${window.location.origin}/integrations/oauth/callback`;
      
      try {
        setActionLoading(prev => ({ ...prev, [platform]: 'connecting' }));
        
        const response = await authFetch(`/api/marketing/integrations/${platform}/oauth/initiate`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ redirect_uri: redirectUri })
        });

        if (!response.ok) {
          throw new Error('Failed to initiate OAuth');
        }

        const data = await response.json();
        
        // Redirect to OAuth provider
        if (data.oauth_data && data.oauth_data.authorization_url) {
          window.location.href = data.oauth_data.authorization_url;
        }

      } catch (err) {
        setError(`OAuth initiation failed: ${err.message}`);
        setActionLoading(prev => ({ ...prev, [platform]: null }));
      }
    } else {
      // Handle API key configuration
      setConfigPlatform(platform);
      setConfigForm({ api_key: '', additional_config: {} });
      setShowConfigModal(true);
    }
  };

  const handleConfigSubmit = async () => {
    try {
      setActionLoading(prev => ({ ...prev, [configPlatform]: 'configuring' }));
      
      const response = await authFetch(`/api/marketing/integrations/${configPlatform}/api-key`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(configForm)
      });

      if (!response.ok) {
        throw new Error('Failed to configure API key');
      }

      setSuccess('Integration configured successfully');
      setShowConfigModal(false);
      setConfigPlatform(null);
      await loadIntegrations();

    } catch (err) {
      setError(`Configuration failed: ${err.message}`);
    } finally {
      setActionLoading(prev => ({ ...prev, [configPlatform]: null }));
    }
  };

  const handleTest = async (platform) => {
    try {
      setActionLoading(prev => ({ ...prev, [platform]: 'testing' }));
      
      const response = await authFetch(`/api/marketing/integrations/${platform}/test`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Connection test failed');
      }

      const data = await response.json();
      
      if (data.test_result && data.test_result.success) {
        setSuccess('Connection test successful');
      } else {
        setError('Connection test failed');
      }

      await loadIntegrations();

    } catch (err) {
      setError(`Test failed: ${err.message}`);
    } finally {
      setActionLoading(prev => ({ ...prev, [platform]: null }));
    }
  };

  const handleSync = async (platform) => {
    try {
      setActionLoading(prev => ({ ...prev, [platform]: 'syncing' }));
      
      const response = await authFetch(`/api/marketing/integrations/${platform}/sync`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Data sync failed');
      }

      setSuccess('Data sync completed successfully');
      await loadIntegrations();

    } catch (err) {
      setError(`Sync failed: ${err.message}`);
    } finally {
      setActionLoading(prev => ({ ...prev, [platform]: null }));
    }
  };

  const handleDisconnect = async (platform) => {
    if (!window.confirm(`Are you sure you want to disconnect ${platform}?`)) {
      return;
    }

    try {
      setActionLoading(prev => ({ ...prev, [platform]: 'disconnecting' }));
      
      const response = await authFetch(`/api/marketing/integrations/${platform}/disconnect`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to disconnect');
      }

      setSuccess('Integration disconnected successfully');
      await loadIntegrations();

    } catch (err) {
      setError(`Disconnect failed: ${err.message}`);
    } finally {
      setActionLoading(prev => ({ ...prev, [platform]: null }));
    }
  };

  const getStatusColor = (platform) => {
    const status = statuses[platform];
    if (!status) return 'disconnected';
    if (status.is_connected && status.status === 'active') return 'connected';
    if (status.error_message) return 'error';
    return 'testing';
  };

  const renderIntegrationCard = (integration) => {
    const status = statuses[integration.platform] || {};
    const isConnected = status.is_connected;
    const currentAction = actionLoading[integration.platform];
    const statusColor = getStatusColor(integration.platform);

    return (
      <IntegrationCard key={integration.platform} $isDark={isDark}>
        <IntegrationHeader $isDark={isDark}>
          <h3>{integration.name}</h3>
          <StatusBadge status={statusColor}>
            {currentAction ? currentAction : (isConnected ? 'Connected' : 'Disconnected')}
          </StatusBadge>
        </IntegrationHeader>

        <IntegrationInfo $isDark={isDark}>
          <p>{integration.description}</p>
          <p><span className="auth-type">Auth Type:</span> {integration.auth_type.toUpperCase()}</p>
          {integration.data_types && integration.data_types.length > 0 && (
            <p><span className="auth-type">Data Types:</span> {integration.data_types.join(', ')}</p>
          )}
        </IntegrationInfo>

        <ActionButtons>
          {!isConnected ? (
            <button 
              className="connect-btn"
              onClick={() => handleConnect(integration.platform, integration.auth_type)}
              disabled={currentAction}
            >
              {currentAction === 'connecting' || currentAction === 'configuring' ? 'Connecting...' : 'Connect'}
            </button>
          ) : (
            <>
              <button 
                className="test-btn"
                onClick={() => handleTest(integration.platform)}
                disabled={currentAction}
              >
                {currentAction === 'testing' ? 'Testing...' : 'Test'}
              </button>
              
              <button 
                className="sync-btn"
                onClick={() => handleSync(integration.platform)}
                disabled={currentAction}
              >
                {currentAction === 'syncing' ? 'Syncing...' : 'Sync'}
              </button>
              
              <button 
                className="disconnect-btn"
                onClick={() => handleDisconnect(integration.platform)}
                disabled={currentAction}
              >
                {currentAction === 'disconnecting' ? 'Disconnecting...' : 'Disconnect'}
              </button>
            </>
          )}
        </ActionButtons>

        {isConnected && status && (
          <ConnectionDetails $isDark={isDark}>
            {status.connection_date && (
              <div className="detail-row">
                <span className="label">Connected:</span>
                <span className="value">{new Date(status.connection_date).toLocaleDateString()}</span>
              </div>
            )}
            {status.last_sync && (
              <div className="detail-row">
                <span className="label">Last Sync:</span>
                <span className="value">{new Date(status.last_sync).toLocaleString()}</span>
              </div>
            )}
            {status.data_synced && (
              <div className="detail-row">
                <span className="label">Data Synced:</span>
                <span className="value">{status.data_synced.toLocaleString()}</span>
              </div>
            )}
            {status.api_quota_used !== undefined && status.api_quota_limit && (
              <div className="detail-row">
                <span className="label">API Usage:</span>
                <span className="value">{status.api_quota_used} / {status.api_quota_limit}</span>
              </div>
            )}
          </ConnectionDetails>
        )}
      </IntegrationCard>
    );
  };

  return (
    <IntegrationsContainer>
      {/* Breadcrumb Navigation */}
      <nav className="breadcrumb">
        <button
          onClick={() => window.location.href = '/'}
          className="breadcrumb-link"
        >
          Dashboard
        </button>
        <span className="breadcrumb-separator">/</span>
        <span className="breadcrumb-current">
          Integrations
        </span>
      </nav>

        {/* Main Card */}
        <div className="card" style={{ background: isDark ? '#1f2937' : 'white', border: `1px solid ${isDark ? '#374151' : 'var(--gray-200)'}` }}>
          {/* Header */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '1.5rem',
            paddingBottom: '1.5rem',
            borderBottom: '3px solid #EF4444'
          }}>
            <div>
              <h2 style={{
                color: isDark ? '#f9fafb' : 'var(--gray-900)',
                fontSize: '1.5rem',
                fontWeight: 'bold',
                margin: '0 0 0.25rem 0'
              }}>
                External Integrations
              </h2>
              <p style={{
                color: isDark ? '#9ca3af' : 'var(--gray-600)',
                margin: 0,
                fontSize: '0.875rem'
              }}>
                Connect and manage your external marketing platforms
              </p>
            </div>
            <RefreshButton onClick={loadIntegrations} disabled={loading}>
              {loading ? 'Loading...' : 'Refresh'}
            </RefreshButton>
          </div>

        {error && <ErrorMessage>{error}</ErrorMessage>}
        {success && <SuccessMessage>{success}</SuccessMessage>}

        {/* AI Providers Section */}
        <div style={{ marginBottom: '2rem' }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '1rem',
            paddingBottom: '0.5rem',
            borderBottom: '2px solid #6c5ce7'
          }}>
            <h3 style={{ margin: 0, color: isDark ? '#f9fafb' : '#333', fontSize: '1.3rem' }}>AI Providers</h3>
            <button
              onClick={handleAddAiIntegration}
              style={{
                background: '#6c5ce7',
                color: 'white',
                border: 'none',
                padding: '0.6rem 1.2rem',
                borderRadius: '6px',
                cursor: 'pointer',
                fontWeight: '500'
              }}
            >
              + Add AI Provider
            </button>
          </div>

          {aiIntegrations.length === 0 ? (
            <div style={{
              background: isDark ? '#374151' : '#f8f9fa',
              padding: '2rem',
              borderRadius: '8px',
              textAlign: 'center',
              color: isDark ? '#9ca3af' : '#666'
            }}>
              No AI providers configured. Click "Add AI Provider" to get started.
            </div>
          ) : (
            <IntegrationsGrid>
              {aiIntegrations.map(integration => (
                <IntegrationCard key={integration.id} $isDark={isDark}>
                  <IntegrationHeader $isDark={isDark}>
                    <div>
                      <h3>{integration.display_name}</h3>
                      <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.85rem', color: isDark ? '#9ca3af' : '#666' }}>
                        {integration.platform.toUpperCase()}
                      </p>
                    </div>
                    <StatusBadge status={integration.is_active ? 'connected' : 'disconnected'}>
                      {integration.is_active ? 'Active' : 'Inactive'}
                    </StatusBadge>
                  </IntegrationHeader>

                  {integration.has_credentials && (
                    <IntegrationInfo $isDark={isDark}>
                      <p style={{ color: '#28a745', fontWeight: '500' }}>✓ API Key Configured</p>
                    </IntegrationInfo>
                  )}

                  {integration.sync_status && (
                    <IntegrationInfo $isDark={isDark}>
                      <p>
                        <span className="auth-type">Status:</span> {integration.sync_status}
                      </p>
                      {integration.error_message && (
                        <p style={{ color: '#dc3545', fontSize: '0.85rem' }}>
                          {integration.error_message}
                        </p>
                      )}
                    </IntegrationInfo>
                  )}

                  <ActionButtons>
                    <button 
                      className="test-btn"
                      onClick={() => handleTestAiIntegration(integration.id)}
                      disabled={actionLoading[`ai_${integration.id}`]}
                    >
                      {actionLoading[`ai_${integration.id}`] === 'testing' ? 'Testing...' : 'Test'}
                    </button>
                    
                    <button 
                      className="configure-btn"
                      onClick={() => handleEditAiIntegration(integration)}
                    >
                      Edit
                    </button>
                    
                    <button 
                      className="disconnect-btn"
                      onClick={() => handleDeleteAiIntegration(integration.id)}
                    >
                      Delete
                    </button>
                  </ActionButtons>

                  {integration.last_sync && (
                    <ConnectionDetails $isDark={isDark}>
                      <div className="detail-row">
                        <span className="label">Last Tested:</span>
                        <span className="value">{new Date(integration.last_sync).toLocaleString()}</span>
                      </div>
                    </ConnectionDetails>
                  )}
                </IntegrationCard>
              ))}
            </IntegrationsGrid>
          )}
        </div>

        {/* Marketplace Integrations Section */}
        <div style={{ marginBottom: '2rem' }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '1rem',
            paddingBottom: '0.5rem',
            borderBottom: '2px solid #FF6B6B'
          }}>
            <h3 style={{ margin: 0, color: isDark ? '#f9fafb' : '#333', fontSize: '1.3rem' }}>Marketplace Integrations</h3>
            <button
              onClick={handleAddMarketplaceIntegration}
              style={{
                background: '#FF6B6B',
                color: 'white',
                border: 'none',
                padding: '0.6rem 1.2rem',
                borderRadius: '6px',
                cursor: 'pointer',
                fontWeight: '500'
              }}
            >
              + Add Marketplace
            </button>
          </div>

          {marketplaceIntegrations.length === 0 ? (
            <div style={{
              background: isDark ? '#374151' : '#f8f9fa',
              padding: '2rem',
              borderRadius: '8px',
              textAlign: 'center',
              color: isDark ? '#9ca3af' : '#666'
            }}>
              No marketplace integrations configured. Click "Add Marketplace" to connect to Amazon, AliExpress, Temu, Shein, Magento 2, or WooCommerce.
            </div>
          ) : (
            <IntegrationsGrid>
              {marketplaceIntegrations.map(integration => (
                <IntegrationCard key={integration.id} $isDark={isDark}>
                  <IntegrationHeader $isDark={isDark}>
                    <div>
                      <h3>{integration.display_name}</h3>
                      <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.85rem', color: isDark ? '#9ca3af' : '#666' }}>
                        {integration.platform.toUpperCase()}
                      </p>
                    </div>
                    <StatusBadge status={integration.is_active ? 'connected' : 'disconnected'}>
                      {integration.is_active ? 'Active' : 'Inactive'}
                    </StatusBadge>
                  </IntegrationHeader>

                  {integration.has_credentials && (
                    <IntegrationInfo $isDark={isDark}>
                      <p style={{ color: '#28a745', fontWeight: '500' }}>✓ Credentials Configured</p>
                    </IntegrationInfo>
                  )}

                  <ActionButtons>
                    <button 
                      className="test-btn"
                      onClick={() => handleTestMarketplaceIntegration(integration.id)}
                      disabled={actionLoading[`marketplace_${integration.id}`]}
                    >
                      {actionLoading[`marketplace_${integration.id}`] === 'testing' ? 'Testing...' : 'Test'}
                    </button>
                    
                    <button 
                      className="configure-btn"
                      onClick={() => handleEditMarketplaceIntegration(integration)}
                    >
                      Edit
                    </button>
                    
                    <button 
                      className="disconnect-btn"
                      onClick={() => handleDeleteMarketplaceIntegration(integration.id)}
                    >
                      Delete
                    </button>
                  </ActionButtons>

                  {integration.last_sync && (
                    <ConnectionDetails $isDark={isDark}>
                      <div className="detail-row">
                        <span className="label">Last Tested:</span>
                        <span className="value">{new Date(integration.last_sync).toLocaleString()}</span>
                      </div>
                    </ConnectionDetails>
                  )}
                </IntegrationCard>
              ))}
            </IntegrationsGrid>
          )}
        </div>

        {/* Marketing & CRM Integrations Section */}
        <div style={{ marginBottom: '2rem' }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '1rem',
            paddingBottom: '0.5rem',
            borderBottom: '2px solid #4CAF50'
          }}>
            <h3 style={{ margin: 0, color: isDark ? '#f9fafb' : '#333', fontSize: '1.3rem' }}>Marketing & CRM Integrations</h3>
          </div>

        {loading ? (
          <LoadingSpinner>
            <div className="spinner"></div>
          </LoadingSpinner>
        ) : (
          <IntegrationsGrid>
            {integrations.map(renderIntegrationCard)}
          </IntegrationsGrid>
        )}
        </div>
        </div>

        {showConfigModal && (
          <ConfigModal>
            <ModalContent $isDark={isDark}>
              <h3>Configure {configPlatform}</h3>
              
              <div className="form-group">
                <label>API Key:</label>
                <input
                  type="password"
                  value={configForm.api_key}
                  onChange={(e) => setConfigForm(prev => ({ ...prev, api_key: e.target.value }))}
                  placeholder="Enter your API key"
                />
              </div>

              <div className="form-group">
                <label>Additional Configuration (JSON):</label>
                <textarea
                  rows={4}
                  value={JSON.stringify(configForm.additional_config, null, 2)}
                  onChange={(e) => {
                    try {
                      const config = JSON.parse(e.target.value || '{}');
                      setConfigForm(prev => ({ ...prev, additional_config: config }));
                    } catch (err) {
                      // Invalid JSON, keep current value
                    }
                  }}
                  placeholder='{"setting1": "value1", "setting2": "value2"}'
                />
              </div>

              <div className="modal-actions">
                <button 
                  className="secondary"
                  onClick={() => {
                    setShowConfigModal(false);
                    setConfigPlatform(null);
                  }}
                >
                  Cancel
                </button>
                <button 
                  className="primary"
                  onClick={handleConfigSubmit}
                  disabled={!configForm.api_key}
                >
                  Configure
                </button>
              </div>
            </ModalContent>
          </ConfigModal>
        )}
      </IntegrationsContainer>
  );
};

export default Integrations;